import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';

function InnerAddressAutocompleteInput({ value, onChange, placeholder, testId, locationBiasText = '', rightElement = null }) {
  const places = useMapsLibrary('places');
  const sessionTokenRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!places || !window.google?.maps?.places) return;
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  }, [places]);

  const fetchSuggestions = (input) => {
    onChange(input, null);
    if (!autocompleteServiceRef.current || input.trim().length < 3) { setSuggestions([]); return; }
    setLoading(true);
    const scopedInput = locationBiasText ? `${input}, ${locationBiasText}, Brasil` : input;
    autocompleteServiceRef.current.getPlacePredictions({ input: scopedInput, componentRestrictions: { country: 'br' }, types: ['address'], sessionToken: sessionTokenRef.current }, (predictions, status) => {
      setLoading(false);
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) { setSuggestions([]); return; }
      setSuggestions(predictions.slice(0, 5));
    });
  };

  const selectSuggestion = (prediction) => {
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails({ placeId: prediction.place_id, fields: ['formatted_address', 'place_id', 'address_components', 'geometry'], sessionToken: sessionTokenRef.current }, (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
      const location = place.geometry?.location;
      const formatted = place.formatted_address || prediction.description || value;
      onChange(formatted, {
        formatted_address: formatted,
        raw_input: value,
        place_id: place.place_id || prediction.place_id || '',
        address_components: place.address_components || [],
        latitude: location?.lat ? location.lat() : null,
        longitude: location?.lng ? location.lng() : null,
      });
      setSuggestions([]);
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    });
  };

  return (
    <div className="relative">
      <Input value={value} onChange={(e) => fetchSuggestions(e.target.value)} placeholder={placeholder} data-testid={testId} autoComplete="off" className={rightElement ? 'pr-10' : ''} />
      {rightElement && <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">{rightElement}</div>}
      {suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-[99999] overflow-hidden rounded-md border border-slate-200 bg-white" data-testid={`${testId}-suggestions`}>
          {suggestions.map(prediction => (
            <button key={prediction.place_id} type="button" onMouseDown={(e) => { e.preventDefault(); selectSuggestion(prediction); }} className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm first:border-t-0 hover:bg-slate-50" data-testid={`${testId}-suggestion-${prediction.place_id}`}>
              <span className="font-semibold text-slate-900">{prediction.structured_formatting?.main_text || prediction.description}</span>
              {prediction.structured_formatting?.secondary_text && <span className="ml-1 text-slate-500">{prediction.structured_formatting.secondary_text}</span>}
            </button>
          ))}
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Buscando...</div>}
        </div>
      )}
    </div>
  );
}

export default function AddressAutocompleteInput(props) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return <div className="relative"><Input value={props.value} onChange={(e) => props.onChange(e.target.value)} placeholder={props.placeholder} data-testid={props.testId} className={props.rightElement ? 'pr-10' : ''} />{props.rightElement && <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">{props.rightElement}</div>}</div>;
  return <APIProvider apiKey={apiKey} libraries={['places']}><InnerAddressAutocompleteInput {...props} /></APIProvider>;
}